import { ReactNode, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import TextInput from '@alltrails/denali/components/TextInput';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import { executeRecaptcha } from '@alltrails/core';
import Button from '@alltrails/denali/components/Button';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import { ApiError, post } from '@alltrails/shared/api';
import logLogInFailed from '@alltrails/amplitude/events/logLogInFailed';
import { useFormatMessage } from '@alltrails/language/hooks/useFormatMessage';
import LoginResponse from '../../types/LoginResponse';
import isValidEmail from '../../utils/isValidEmail';
import { authSuccessCallback } from '../../utils/AuthHandlers';
import styles from './EmailPasswordLogin.module.scss';

const LABELS = defineMessages({
  EMAIL_ADDRESS: {
    defaultMessage: 'Email address'
  },
  PASSWORD: {
    defaultMessage: 'Password'
  }
});

type Props = {
  grantType?: string | null;
  handleLoginSuccess?: ({ user }: LoginResponse) => void;
  returnTo?: string;
};

function validateEmail(value: string) {
  if (!isValidEmail(value)) {
    return <FormattedMessage defaultMessage="Email is not valid." />;
  }

  return undefined; // text field uses undefined
}

function validatePassword(value: string) {
  if (value.length < 6) {
    return <FormattedMessage defaultMessage="Password must be at least 6 characters long." />;
  }

  if (value.length > 128) {
    return <FormattedMessage defaultMessage="Password must not exceed 128 characters." />;
  }

  return undefined; // text field uses undefined
}

export default function EmailPasswordLogin({ grantType, handleLoginSuccess, returnTo = '/' }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userEmailError, setUserEmailError] = useState<ReactNode>();
  const [userPasswordError, setUserPasswordError] = useState<ReactNode>();
  const [serverError, setServerError] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);
  const [showErrorsOnType, setShowErrorsOnType] = useState(false);
  const features = useFeatures();

  const {
    formattedDefaultMessages: { EMAIL_ADDRESS, PASSWORD }
  } = useFormatMessage(LABELS);

  function failedFormValidation() {
    const emailError = validateEmail(userEmail);
    const passwordError = validatePassword(userPassword);

    setUserEmailError(emailError);
    setUserPasswordError(passwordError);

    return emailError || passwordError;
  }

  function handleChange(name: string, value: string) {
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (showErrorsOnType) {
      if (name === 'userEmail') {
        setUserEmailError(validateEmail(value));
      }

      if (name === 'userPassword') {
        setUserPasswordError(validatePassword(value));
      }
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'email' });

    if (submitting) {
      return;
    }

    if (failedFormValidation()) {
      setShowErrorsOnType(true);
      return;
    }

    setSubmitting(true);

    try {
      let recaptcha;

      // on alpha we disable recaptcha for Cypress testing
      if (features.includes('recaptcha_user_signup_login')) {
        recaptcha = await executeRecaptcha('userSignupLogin');
      }

      const response = await (grantType
        ? post<LoginResponse>('/identity/auth/sign_in', {
            grant_type: grantType,
            // recaptcha skipped for now
            user: {
              email: userEmail,
              password: userPassword
            }
          })
        : post<LoginResponse>('/identity/oauth/token', {
            client_id: process.env.NEXT_PUBLIC_IDENTITY_CLIENT_ID,
            username: userEmail,
            grant_type: 'password',
            password: userPassword,
            recaptcha
          }));

      authSuccessCallback(response, 'email', response.is_collab_list_invite);

      if (handleLoginSuccess) {
        handleLoginSuccess(response);
      } else {
        const destinationUrl = returnTo || wrapUrlSafe('/', languageRegionCode);

        window.location.assign(destinationUrl);
      }
    } catch (e: any) {
      if (e instanceof ApiError && e.data?.errors?.length > 0) {
        setServerError(e.data.errors[0].message);
      } else {
        setServerError(<FormattedMessage defaultMessage="Error, please try again" />);
      }

      logLogInFailed({
        authentication_type: 'email',
        failure_type: e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status
      });
    } finally {
      setSubmitting(false);
    }
  }

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleLogin(event);
      }
    },
    [handleLogin]
  );

  return (
    <form onSubmit={handleLogin}>
      <div className={styles.inputItem}>
        <TextInput
          autoFocus
          onChange={(value: string) => {
            handleChange('userEmail', value);
            setUserEmail(value);
          }}
          onKeyDown={onKeyDown}
          errorText={userEmailError}
          placeholder={EMAIL_ADDRESS}
          labelText={EMAIL_ADDRESS}
          name="userEmail"
          type="email"
          value={userEmail}
        />
      </div>
      <div className={styles.inputItem}>
        <TextInput
          onChange={(value: string) => {
            handleChange('userPassword', value);
            setUserPassword(value);
          }}
          onKeyDown={onKeyDown}
          errorText={userPasswordError}
          labelText={PASSWORD}
          placeholder={PASSWORD}
          name="userPassword"
          type="password"
          value={userPassword}
        />
      </div>
      {serverError && <div className={styles.serverError}>{serverError}</div>}
      <Button
        text={<FormattedMessage defaultMessage="Log in" />}
        disabled={submitting}
        testId="email-password-login-button"
        type="submit"
        variant="primary"
        width="full"
      />
    </form>
  );
}
